import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import {Badge, Box, CircularProgress, Divider, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {surveyColorOf} from "crqlar-react-common/dist/utils/surveyColor.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery, useQueryClient} from "react-query";
import InsightsDateRangeControl from "../../insights/v2/base/InsightsDateRangeControl";
import {RadioFilterPanel} from "../../layout/rightSidebar/RadioFilterPanel";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {
    RightSidebarActionButton,
    RightSidebarDivider,
    RightSidebarPanelButton,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import SurveyEvaluationFilters, {
    createDefaultEvaluationFilters,
    SurveyEvaluationFilterState,
} from "../shared/SurveyEvaluationFilters";
import {useAllSurveys} from "../shared/useAllSurveys";
import {useSelectedSurveyId} from "../shared/useSelectedSurveyId";
import {useSurveySectorOptions} from "../shared/useSurveyEvaluation";
import {TargetSetsManagerDialog} from "./dialogs/TargetSetsManagerDialog";
import {ThemesManagerDialog} from "./dialogs/ThemesManagerDialog";
import {CrqlarScoreCard} from "./cards/CrqlarScoreCard";
import {SurveyHealthCard} from "./cards/SurveyHealthCard";
import {ReachCard} from "./cards/ReachCard";
import {SentimentCard} from "./cards/SentimentCard";
import {NpsSplitCard} from "./cards/NpsSplitCard";
import {ThemesCard} from "./cards/ThemesCard";
import {ResponsesPerDayCard} from "./cards/ResponsesPerDayCard";
import {CARD_GAP, CONTENT_MAX_WIDTH} from "./constants";
import {SurveyInsightsBand} from "./SurveyInsightsBand";
import {InsightsRailSearch, matchesTitle} from "../../insights/kit/InsightsRailSearch";
import {KeepTallest} from "./base/KeepTallest";
import {toStatCards} from "./data/surveyTargets.util";
import {useSurveyBreakdown, useSurveyIntroduction, useSurveySeries, useSurveyThemes} from "./data/useSurveyInsightsQueries";
import {SurveySeriesPart} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {BreakdownCard} from "./cards/BreakdownCard";
import {
    SURVEY_BREAKDOWN_QUERY_KEY,
    SURVEY_INTRODUCTION_QUERY_KEY,
    SURVEY_SERIES_QUERY_KEY,
    SURVEY_SUMMARY_QUERY_KEY,
    SURVEY_TARGET_SETS_QUERY_KEY,
    SURVEY_TARGET_SETS_TABLE_ID,
} from "../shared/surveyQueryRefresh";
import {serverTableQueryKey} from "../../common/serverTable";


const SERIES_PARTS = [
    SurveySeriesPart.SCORE,
    SurveySeriesPart.RESPONSES,
    SurveySeriesPart.HEALTH,
    SurveySeriesPart.REACH,
    SurveySeriesPart.SENTIMENT,
    SurveySeriesPart.NPS,
];

function SurveyInsights() {
    const {t} = useTranslation();
    const queryClient = useQueryClient();

    const [filters, setFilters] = useState<SurveyEvaluationFilterState>(createDefaultEvaluationFilters);
    const [targetSetsDialogOpen, setTargetSetsDialogOpen] = useState(false);
    const [themesDialogOpen, setThemesDialogOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [isContentScrolled, setIsContentScrolled] = useState(false);

    const {surveys, isLoading: surveysLoading} = useAllSurveys();
    const {selectedSurveyId, selectSurvey} = useSelectedSurveyId(surveys);

    const surveyQuery = useQuery(
        ['survey', selectedSurveyId],
        () => SurveyService.getSurveyById(selectedSurveyId!),
        {enabled: !!selectedSurveyId, refetchOnWindowFocus: false}
    );
    const survey: Survey | undefined = surveyQuery.data;

    const sectorOptions = useSurveySectorOptions(survey);
    const {introduction, loading: bandLoading} = useSurveyIntroduction(selectedSurveyId, filters);
    const {series, loading: seriesLoading} = useSurveySeries(selectedSurveyId, filters, SERIES_PARTS);
    const {themes, loading: themesLoading} = useSurveyThemes(selectedSurveyId, filters);
    const {breakdown, loading: breakdownLoading} = useSurveyBreakdown(selectedSurveyId, filters);

    const targetSetsQuery = useQuery(
        [SURVEY_TARGET_SETS_QUERY_KEY],
        () => SurveyService.listTargetSets({limit: {offset: 0, limit: 999}}),
        {refetchOnWindowFocus: false}
    );
    const targetSets = useMemo(() => targetSetsQuery.data?.content ?? [], [targetSetsQuery.data]);
    // A target set lists the surveys it applies to; the survey itself knows nothing about targets.
    const activeTargetSet = targetSets.find(set => selectedSurveyId != null && set.surveyIds?.includes(selectedSurveyId));

    const assignTargetSet = async (targetSetId: string | null) => {
        if (survey == null) return;

        await SurveyService.assignSurveyTargetSet({surveyId: survey.id!, targetSetId});
        await Promise.all([
            // The active set and the manager's survey counts both come from the sets.
            queryClient.invalidateQueries([SURVEY_TARGET_SETS_QUERY_KEY]),
            queryClient.invalidateQueries(serverTableQueryKey(SURVEY_TARGET_SETS_TABLE_ID)),
            queryClient.invalidateQueries([SURVEY_INTRODUCTION_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_SERIES_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_BREAKDOWN_QUERY_KEY]),
            queryClient.invalidateQueries([SURVEY_SUMMARY_QUERY_KEY]),
        ]);
    };

    const dateNavigation = (
        <InsightsDateRangeControl
            range={filters.range}
            compareRange={filters.compareRange}
            onChange={selection => setFilters(current => ({
                ...current,
                range: selection.range,
                compareRange: selection.compareRange,
            }))}
        />
    );

    const placeholder = (text: string) => (
        <Typography variant={'body2'} color={'text.secondary'}>
            {text}
        </Typography>
    );

    const content = () => {
        if (surveysLoading) {
            return (
                <Stack alignItems={'center'} sx={{py: 6}}>
                    <CircularProgress size={24}/>
                </Stack>
            );
        }

        if (!surveys.length) {
            return placeholder(t('No surveys yet. Create one in the settings to see its results here.'));
        }

        const capped = {width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto'} as const;

        const shows = (key: string) => matchesTitle(query, t(key), key);

        const showScore = shows('Crqlar score');
        const showHealth = shows('Survey health');
        const showReach = shows('Reach');
        const showSentiment = shows('Sentiment');
        const showResponses = shows('Responses per day');
        const showNps = shows('NPS split');
        const showThemes = matchesTitle(query, t('Themes', {context: 'survey'}), 'Themes');
        const showBreakdown = shows('Breakdown');

        const showAnyCard = showScore || showHealth || showReach || showSentiment || showResponses || showNps || showThemes || showBreakdown;

        const bandMatches = (title: string, key: string) => matchesTitle(query, title, key);
        const showBand = bandMatches(survey?.name ?? '', survey?.name ?? '')
            || toStatCards(introduction, t).some(stat => bandMatches(stat.title, stat.id));

        if (!showBand && !showAnyCard) {
            return (
                <Typography variant={'body2'} color={'text.secondary'} sx={{py: 6, textAlign: 'center'}}>
                    {t('No insights match your search.')}
                </Typography>
            );
        }

        const column = (node: React.ReactNode) => (
            <Box sx={{flex: 1, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column'}}>
                <Box sx={{flex: 1, minHeight: 0}}>{node}</Box>
            </Box>
        );

        return (
            <Stack gap={CARD_GAP} sx={{...capped, minWidth: 0}}>
                {showBand && (
                    <SurveyInsightsBand
                        introduction={introduction}
                        color={survey ? surveyColorOf(survey) : undefined}
                        loading={bandLoading}
                        matches={bandMatches}
                    />
                )}

                {showScore && <CrqlarScoreCard series={series?.score ?? undefined} loading={seriesLoading}/>}

                {showResponses && <ResponsesPerDayCard series={series?.responses ?? undefined} loading={seriesLoading}/>}

                {(showHealth || showReach) && (
                    <Stack id={'insights-health'} direction={{xs: 'column', lg: 'row'}} alignItems={'stretch'} gap={CARD_GAP}>
                        {showHealth && column(<SurveyHealthCard series={series?.health ?? undefined} loading={seriesLoading}/>)}
                        {showReach && column(<ReachCard series={series?.reach ?? undefined} loading={seriesLoading}/>)}
                    </Stack>
                )}

                {(showSentiment || showNps) && (
                    <Stack id={'insights-sentiment'} direction={{xs: 'column', lg: 'row'}} alignItems={'stretch'} gap={CARD_GAP}>
                        {showSentiment && column(<SentimentCard series={series?.sentiment ?? undefined} loading={seriesLoading}/>)}
                        {showNps && column(<NpsSplitCard series={series?.nps ?? undefined} loading={seriesLoading}/>)}
                    </Stack>
                )}

                {showBreakdown && (
                    <KeepTallest key={selectedSurveyId}>
                        <BreakdownCard breakdown={breakdown} loading={breakdownLoading}/>
                    </KeepTallest>
                )}

                {showThemes && <ThemesCard series={themes} loading={themesLoading}/>}
            </Stack>
        );
    };

    const selectedSurvey = surveys.find(item => item.id === selectedSurveyId);
    const activeFilterCount = [
        filters.languages.length > 0,
        filters.sectorIds.length > 0,
        filters.reviewStates.length > 0,
        filters.guestCategoryId != null,
        filters.versions.length > 0,
        filters.minSubmissions != null,
    ].filter(Boolean).length;

    return (
        <>
            <RightSidebarRailLayout>
                <RightSidebarRailHeader>
                    <RightSidebarRailHeaderCenter>
                        {dateNavigation}
                    </RightSidebarRailHeaderCenter>
                </RightSidebarRailHeader>

                <Divider sx={{borderColor: isContentScrolled ? 'divider' : 'transparent'}}/>

                <Box
                    onScroll={event => setIsContentScrolled(event.currentTarget.scrollTop > 0)}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: 'auto',
                        pt: {xs: 2, md: 3},
                        pb: {xs: 6, md: 9},
                        px: {xs: 2, md: 3},
                    }}
                >
                    <Stack gap={CARD_GAP}>
                        {content()}
                    </Stack>
                </Box>

                <RightSidebarRail>
                    <InsightsRailSearch query={query} onQueryChange={setQuery}/>

                    <RightSidebarPanelButton
                        icon={<PollOutlinedIcon/>}
                        label={selectedSurvey?.name || t('Survey')}
                        renderPanel={close => (
                            <RadioFilterPanel
                                title={t('Survey')}
                                options={surveys.map(item => ({value: item.id, label: item.name || item.id, color: surveyColorOf(item)}))}
                                value={selectedSurveyId ?? ''}
                                onChange={surveyId => {
                                    selectSurvey(surveyId);
                                    close();
                                }}
                                onClose={close}
                            />
                        )}
                    />
                    <RightSidebarPanelButton
                        icon={(
                            <Badge
                                color="primary"
                                badgeContent={activeFilterCount}
                                overlap="circular"
                                invisible={activeFilterCount === 0}
                            >
                                <FilterAltOutlinedIcon/>
                            </Badge>
                        )}
                        label={t('Filters')}
                        renderPanel={close => (
                            <RightSidebarFloatingPanel sx={{overflow: 'hidden'}}>
                                <RightSidebarPanelHeader title={t('Filters')} onClose={close}/>
                                <Stack sx={{flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 1, pb: 2}}>
                                    <SurveyEvaluationFilters
                                        value={filters}
                                        onChange={setFilters}
                                        languageOptions={survey?.languages ?? []}
                                        sectorOptions={sectorOptions}
                                        currentVersion={survey?.version}
                                        layout={'stacked'}
                                    />
                                </Stack>
                            </RightSidebarFloatingPanel>
                        )}
                    />

                    <RightSidebarDivider/>

                    <RightSidebarActionButton
                        icon={(
                            <Badge
                                color="success"
                                variant="dot"
                                overlap="circular"
                                invisible={activeTargetSet == null}
                                sx={theme => ({
                                    '& .MuiBadge-badge': {
                                        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                                    },
                                })}
                            >
                                <TrackChangesOutlinedIcon/>
                            </Badge>
                        )}
                        label={activeTargetSet?.name || t('Targets')}
                        disabled={!survey}
                        onClick={() => setTargetSetsDialogOpen(true)}
                    />

                    <RightSidebarActionButton
                        icon={<TipsAndUpdatesOutlinedIcon/>}
                        label={t('Themes', {context: 'survey'})}
                        onClick={() => setThemesDialogOpen(true)}
                    />
                </RightSidebarRail>
            </RightSidebarRailLayout>

            <TargetSetsManagerDialog
                open={targetSetsDialogOpen}
                onClose={() => setTargetSetsDialogOpen(false)}
                survey={survey}
                activeTargetSetId={activeTargetSet?.id ?? null}
                onAssign={assignTargetSet}
            />

            <ThemesManagerDialog open={themesDialogOpen} onClose={() => setThemesDialogOpen(false)}/>
        </>
    );
}

export default SurveyInsights;
